import {useState} from 'react'
import {Pressable, StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {FeedbackBar} from '#/components/capsules/play/FeedbackBar'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {
  type FBState,
  type SlidePlayProps,
} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayOptionTextStyle,
  usePlayPrimaryAction,
  usePlaySelectionMode,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

const ACCENT = SLIDE_ACCENT.mark_words

const cleanW = (word: string) =>
  word
    .replace(/[.,!?;:()«»]/g, '')
    .toLowerCase()
    .trim()

export function MarkWordsSlidePlay({
  slide,
  onAnswer,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The passage wears the book's own hand, so marking words feels like an
  // exercise on the page rather than text rendered by an app.
  const answerText = usePlayOptionTextStyle()
  // A whole number of the page's rules between the question and the passage.
  const stackGap = usePlayStackGap(16)
  // A word in the passage is one line of writing: on ruled paper the marked
  // words have to keep starting on the lines as the passage wraps.
  const lineBox = answerText.lineHeight ?? 22
  // The check button is furniture on the page, not writing: whole rules tall.
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // Inside a book the block owns the surface — printed straight on an open or
  // ruled page, framed by the block's own panel otherwise — so this widget only
  // draws a box of its own outside a book.
  const onPaper = usePlaySuppressOwnSurface()
  // Inside a book every selection affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // A marked word is marked with the highlighter itself — the stroke is the
  // gesture, so the box around the word is given up.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()

  const rawText = slide.markWordsText ?? ''
  const correctWords = (slide.markWordsCorrect ?? []).map(w =>
    w.toLowerCase().trim(),
  )
  const tokens = rawText.split(/\s+/).filter(Boolean)

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [checked, setChecked] = useState(false)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelected(new Set())
    setChecked(false)
  }

  const toggle = (i: number) => {
    if (checked) return
    inkSound.mark()
    setSelected(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  const selWords = [...selected].map(i => cleanW(tokens[i] ?? ''))
  const corrSel = selWords.filter(w => correctWords.includes(w)).length
  const wrongSel = selWords.filter(w => !correctWords.includes(w)).length
  const maxPts = slide.points ?? correctWords.length
  const pts = Math.max(
    0,
    Math.round(
      ((corrSel - wrongSel) / Math.max(correctWords.length, 1)) * maxPts,
    ),
  )
  const allOk = corrSel === correctWords.length && wrongSel === 0
  const fbState: FBState = allOk ? 'correct' : corrSel > 0 ? 'partial' : 'wrong'

  const handleCheck = () => {
    onAnswer({
      isCorrect: allOk,
      points: pts,
      maxPoints: maxPts,
      solutionText: l`Correct words: ${(slide.markWordsCorrect ?? []).join(', ')}`,
      explanation: slide.explanation,
      userAnswer: selWords,
    })
    setChecked(true)
  }

  const getTokenStyle = (i: number) => {
    const isSel = selected.has(i)
    const cw = cleanW(tokens[i] ?? '')
    const isCorrectW = correctWords.includes(cw)

    if (checked) {
      if (isSel && isCorrectW)
        return {
          borderColor: palette.positive,
          backgroundColor: palette.positive + '33',
        }
      if (isSel && !isCorrectW)
        return {
          borderColor: palette.negative,
          backgroundColor: palette.negative + '33',
        }
      // A correct word the learner missed — a hint, not a verdict.
      if (!isSel && isCorrectW)
        return {
          borderColor: palette.warning,
          backgroundColor: palette.warning + '22',
        }
      return {borderColor: 'transparent', backgroundColor: 'transparent'}
    }
    if (isSel) return {borderColor: accent, backgroundColor: accent + '33'}
    return {borderColor: 'transparent', backgroundColor: 'transparent'}
  }

  /**
   * The marker ink a word wears, or nothing when it is unmarked.
   *
   * The three states a marked word can be in are the same three as everywhere
   * else in the book: struck and right, struck and wrong, or struck by the
   * writer and not yet judged.
   */
  const markFor = (i: number) => {
    if (!highlight) return null
    const isSel = selected.has(i)
    const isCorrectW = correctWords.includes(cleanW(tokens[i] ?? ''))
    if (checked) {
      if (isSel) return isCorrectW ? palette.positive : palette.negative
      return null
    }
    return isSel ? accent : null
  }

  return (
    <PlayScroller
      style={ss.scroll}
      contentContainerStyle={[ss.content, {padding, gap: stackGap}]}>
      {!!slide.question && (
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>
      )}

      <View
        style={[
          ss.textCard,
          onPaper
            ? {
                backgroundColor: 'transparent',
                // The frame goes, not just its colour: a transparent 1px border
                // still takes two pixels of height, and two pixels is enough to
                // push the whole passage half a rule off the paper.
                borderWidth: 0,
                padding: 0,
                rowGap: 0,
              }
            : {
                backgroundColor: palette.surfaceAlt,
                borderColor: palette.border,
              },
        ]}>
        {tokens.map((word, i) => (
          <Pressable
            key={i}
            onPress={() => toggle(i)}
            accessibilityRole="button"
            style={[
              ss.token,
              getTokenStyle(i),
              highlight && {borderColor: 'transparent'},
              onPaper && {
                height: lineBox,
                paddingVertical: 0,
                marginBottom: 0,
                borderRadius: 4,
                justifyContent: 'center',
              },
            ]}>
            <Text style={[ss.tokenText, answerText, {color: palette.body}]}>
              <Text style={ink(markFor(i))}>{word}</Text>
            </Text>
          </Pressable>
        ))}
      </View>

      {!checked && (
        <TouchableOpacity
          style={[
            ss.checkBtn,
            {
              backgroundColor:
                selected.size > 0 ? primaryColor : palette.surfaceAlt,
              opacity: selected.size > 0 ? 1 : 0.5,
            },
            action.style,
          ]}
          onPress={handleCheck}
          disabled={selected.size === 0}
          accessibilityRole="button"
          accessibilityLabel={l`Check answer`}
          accessibilityHint={undefined}>
          <Feather
            name="check"
            size={16}
            color={
              action.ink(selected.size > 0) ??
              (selected.size > 0 ? palette.onAccent : palette.muted)
            }
          />
          <Text
            style={[
              ss.checkBtnText,
              {
                color:
                  action.ink(selected.size > 0) ??
                  (selected.size > 0 ? palette.onAccent : palette.muted),
              },
              action.labelStyle(selected.size > 0),
            ]}>
            <Trans>Check</Trans>
          </Text>
        </TouchableOpacity>
      )}

      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={slide.explanation}
          solutionText={l`Correct words: ${(slide.markWordsCorrect ?? []).join(', ')}`}
        />
      )}
    </PlayScroller>
  )
}

const ss = StyleSheet.create({
  scroll: {flex: 1},
  content: {padding: 24, gap: 16, paddingBottom: 40},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  textCard: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  token: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1.5,
    marginRight: 2,
    marginBottom: 2,
  },
  tokenText: {color: '#fff', fontSize: 15, lineHeight: 22},
  checkBtn: {
    flexDirection: 'row',
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
